import { KeyboardEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { gql, useQuery } from '@apollo/client';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import classNames from 'classnames';

import * as K from 'common/constants';
import { getRandomNumber } from 'common/functions';
import { numericStringCompare, quote, unquote } from 'common/util/string';
import { filterList } from 'common/util/array';
import { updateSourcesAndLogs } from 'common/util/sourcesAndLogs';
import { isComplexExpression } from 'common/util/filter/parse';
import { getRequestId } from 'common/util/lrqRequest';
import {
  FacetInfo,
  QueryOrigin,
  QueryRequestInput,
  QueryType,
  Value,
} from 'common/graphqlTypes';
import { SourceAndLogData } from 'common/types';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { generateFacetValuesRequest } from 'hooks/useLRQ/useLRQ';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useRecoilCleanup } from 'hooks/useRecoilCleanup';

import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import {
  completeFilterState,
  queryTimeRangeState,
  searchParamRecordState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import { isStandardViewState, viewState } from 'components/common/Managers/ViewStateManager.state';
import SearchFields from 'components/search/Sidebar/SearchFields';
import SourceSelector from 'components/search/Sidebar/SourceSelector';
import FacetList from 'components/search/Sidebar/FacetList';
import FacetDetails from 'components/search/Sidebar/FacetDetails';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import {
  facetValuesRequestsState,
  initialLogRequestsState,
  isFacetDetailsVisibleState,
  plotDataRequestsState,
  powerQueryRequestsState,
  selectedFacetState,
  showFacetDetailsState,
  sidebarStatus,
  useProgressiveStateManagement,
} from 'components/search/SearchLogsPage/SearchStateManager.state';

import './Sidebar.scss';

const SOURCES_QUERY = gql`
  query sources($showOld: Boolean!){
    sources(showOld: $showOld) {
      hosts {
        logFiles
        name
      }
      kubeClusters {
        controllers {
          name
          kind
        }
        name
      }
    }
  }
`;

type SidebarProps = {
  mode?: string;
};

/**
 * This component acts as a placeholder while the data is being loaded
 */
function SidebarSkeleton(): JSX.Element {
  const title = <div style={{ height: 18, width: 46 }} className="Sidebar__Skeleton__Item" />;
  const timelineData = useMemo(() => [...Array(50)].map(() => getRandomNumber(40, 130)), []);
  const facetList = timelineData.map((facetTitleSize, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={index} className="Sidebar__Skeleton__Item--Facet">
      <div style={{ height: 14, width: facetTitleSize }} className="Sidebar__Skeleton__Item" />
      <div style={{ height: 14, width: 20 }} className="Sidebar__Skeleton__Item" />
    </div>
  ));

  return (
    <div className="Sidebar__FacetListContainer Sidebar__Skeleton skeleton-opacity-animation">
      {title}
      {facetList}
    </div>
  );
}

/**
 * This component provides a way for the user to search for and to explore events based on facets.
 * It includes combo boxes to specify host/cluster and logfile/deployment, and a list of facets
 * that appeared in the search results.
 *
 * @prop mode        events mode of the page displaying the sidebar
 */
function Sidebar({ mode }: SidebarProps): JSX.Element {

  const setSidebarStatus = useSetRecoilState(sidebarStatus);

  const initialLogRequests = useRecoilValue(initialLogRequestsState);
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const powerQueryRequests = useRecoilValue(powerQueryRequestsState);
  const view = useRecoilValue(viewState);
  const isStandardView = useRecoilValue(isStandardViewState);

  const isNewSearch = useIsNewSearch();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const completeFilter = useRecoilValue(completeFilterState);
  const teamEmails = useRecoilValue(teamEmailsState);
  const { startTimeNs, endTimeNs } = queryTimeRange;

  let requests;
  if (mode === K.EVENT_MODE.GRAPH) {
    requests = plotDataRequests;
  } else if (mode === K.EVENT_MODE.LOG) {
    requests = initialLogRequests;
  } else {
    requests = powerQueryRequests;
  }
  const facetDataId = getRequestId(requests, QueryType.TOP_FACETS);
  const eventCacheIds = [facetDataId];
  let eventCache = useEventCache(eventCacheIds);
  const doesExistFacetData = !!eventCache?.results?.[0]?.facetData?.facets;
  const doesExistErrorData = !!eventCache?.errors?.[0];
  const filter = searchParams[K.URL_PARAM.FILTER];
  const isComplex = isComplexExpression(filter);
  const returnPreviousCache = !doesExistFacetData && !doesExistErrorData && !isComplex;

  eventCache = useProgressiveStateManagement(eventCache, eventCacheIds, sidebarStatus, returnPreviousCache);
  const { results: [{ facetData }] } = eventCache;
  const isPreviousDataAvailable = facetData.facets !== undefined; // to know when to show skeleton UI

  const [ facetList, setFacetList ] = useState<Array<FacetInfo>>([]);
  const [ facetMap, setFacetMap ] = useState<Record<string, FacetInfo>>({});
  const [ facetFilter, setFacetFilter ] = useState('');
  const [ selectedFacet, setSelectedFacet ] = useRecoilState(selectedFacetState);
  const [ selectedFacetData, setSelectedFacetData ] = useState<FacetInfo | null>(null);
  const [ showOverlay, setShowOverlay ] = useRecoilState(showFacetDetailsState);
  const isFacetDetailsVisible = useRecoilValue(isFacetDetailsVisibleState);
  const [ showingMoreValues, setShowingMoreValues ] = useState<Record<string, boolean>>(K.EMPTY_OBJECT);

  const [ facetValuesRequests, setFacetValuesRequests ] = useRecoilState(facetValuesRequestsState);
  const [ facetValuesRequestMade, setFacetValuesRequestMade ] = useState<Record<string, boolean>>(K.EMPTY_OBJECT);
  const facetValuesDataId = getRequestId(facetValuesRequests, QueryType.FACET_VALUES);
  const facetValuesResult = useEventCache([ facetValuesDataId ]);

  const [ sourcesAndLogs, setSourcesAndLogs ] = useState<SourceAndLogData>(K.EMPTY_OBJECT as SourceAndLogData);

  const listRef = useRef<HTMLDivElement>(null);
  useRecoilCleanup(selectedFacetState, showFacetDetailsState);

  // fetch sources and logs, parse into optimized format
  const { data } = useQuery(
    SOURCES_QUERY,
    // When the view has sources, we don't need to fetch this data as we show Source Selector instead
    { variables: { showOld: false }, skip: Boolean(view.sources) },
  );
  const prevData = usePrevious(data);
  useConditionalEffect(() => {
    setSourcesAndLogs(updateSourcesAndLogs(data.sources));
  }, !!data && !isEqual(prevData, data));

  // figure out if we're still waiting for facet data
  // TODO: see FRONT-3851
  const { results, errors } = useEventCache([ facetDataId ]);
  const loadingFacets = results && results[0] && !results[0].isDone;
  const requestCanceled = initialLogRequests.some((request) => request?.canceled);
  const hasErrors = errors?.some((err) => err != null);

  // If we got new facet data, rebuild the facets list and update the global list of known facets.
  // TODO: replace the expensive isEqual() check with a reference check when the events cache hook has been fixed
  // {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4468}
  const prevFacetData = usePrevious(facetData);
  const prevFacetFilter = usePrevious(facetFilter);
  useConditionalEffect(() => {
    let allFacets: Array<FacetInfo> = [];
    if (facetData && facetData.facets) {
      allFacets = cloneDeep(facetData.facets);
      // string values come in surrounded by double quotes; change those to single quotes
      allFacets.forEach((facet) => {
        facet.values = facet.values.map((value) => {
          const val = value.value;
          if (isString(val) && val[0] === '"' && val[val.length - 1] === '"') {
            return {
              count: value.count,
              value: quote(unquote(val)),
            } as Value;
          }
          return value;
        });
      });
      allFacets.sort((a: FacetInfo, b: FacetInfo) => numericStringCompare(a.name, b.name));
      if (facetFilter) {
        allFacets = filterList(allFacets, facetFilter, 0, (facet) => [ facet.name ]);
      }
    }
    setFacetList(allFacets);
    const facetsMap: Record<string, FacetInfo> = {};
    allFacets.forEach((facet: FacetInfo) => {
      facetsMap[facet.name] = facet;
    });
    // If this is a view with sources, and there were no results for the dataSource facet, we need to provide
    // dummy data for the source selector
    if (view.sources && !facetsMap[view.sources.sourceField]) {
      facetsMap[view.sources.sourceField] = {
        isNumeric: false,
        matchCount: 0,
        name: view.sources.sourceField,
        sampledMatchCount: 0,
        uniqueValuesCount: view.sources.sourceValues.length,
        values: view.sources.sourceValues.map(v => ({ count: 0, value: v })),
      };
    }
    setFacetMap(facetsMap);
  }, !isEqual(facetData, prevFacetData) || facetFilter !== prevFacetFilter);

  // Check to see if we got a FACET_VALUES response
  const facetWithMoreValues = facetValuesResult?.results?.[0]?.facetData?.facets?.[0];
  const prevFacetWithMoreValues = usePrevious(facetWithMoreValues);
  useConditionalEffect(() => {
    const facetWithMoreValuesName = facetWithMoreValues?.name;
    if (!facetWithMoreValuesName || !facetMap[facetWithMoreValuesName]) {
      return;
    }
    const moreFacetData = cloneDeep(facetWithMoreValues);
    if (facetMap[facetWithMoreValuesName].matchCount) {
      // workaround for incorrect FacetValues match count (FRONT-3458)
      moreFacetData.matchCount = facetMap[facetWithMoreValuesName].matchCount;
    }
    setSelectedFacetData(moreFacetData);
    facetMap[facetWithMoreValuesName] = moreFacetData;
    setShowingMoreValues((draft) => ({ ...draft, [facetWithMoreValuesName]: true }));
  }, facetWithMoreValues !== prevFacetWithMoreValues);

  // clear state when a search has been run
  useConditionalEffect(() => {
    setSelectedFacet('');
    setShowOverlay(false);
    setShowingMoreValues(K.EMPTY_OBJECT);
    setFacetValuesRequests(K.EMPTY_ARRAY);
    setFacetValuesRequestMade(K.EMPTY_OBJECT);
  }, isNewSearch);

  const onFacetSelect = (facet = ''): void => {
    setSelectedFacet(facet);
    setSelectedFacetData(facetMap[facet]);
  };

  const onFacetHover = (facet = ''): void => {
    if (!facet) {
      return;
    }
    if (facet !== selectedFacet) {
      setSelectedFacet('');
    }
    if (showOverlay) {
      onFacetSelect(facet);
    }
  };

  // allow user to change selected facet via arrow keys
  const onKeyDown = (event: KeyboardEvent): void => {
    const key = event.keyCode;
    if (showOverlay && (key === K.KEY_CODE.UP_ARROW || key === K.KEY_CODE.DOWN_ARROW)) {
      const facetIdx = facetList.findIndex((facet) => facet.name === selectedFacet);
      const newIdx = (key === K.KEY_CODE.UP_ARROW) ? facetIdx - 1 : facetIdx + 1;
      const newFacet = facetList[newIdx];
      if (newFacet) {
        onFacetSelect(newFacet.name);
        event.preventDefault();
      }
    }
  };

  const getMoreFacetValues = (facetName = '', skipSelectedCheck = false): void => {
    if (facetName && (facetName === selectedFacet || skipSelectedCheck)) {
      let origin;
      if (mode === K.EVENT_MODE.GRAPH) {
        origin = QueryOrigin.GRAPH;
      }
      if (mode === K.EVENT_MODE.LOG) {
        origin = QueryOrigin.SEARCH;
      }
      const request = generateFacetValuesRequest({
        facet:     facetName,
        filter:    completeFilter,
        origin,
        startTime: startTimeNs,
        endTime:   endTimeNs,
        teamEmails,
      } as unknown as QueryRequestInput);

      setFacetValuesRequestMade((draft) => ({ ...draft, [facetName]: true }));
      setFacetValuesRequests([ request ]);
    }
  };

  const closeDetailsPopup = (): void => {
    setShowOverlay(false);
    setSelectedFacet('');
  };

  const fetchingMoreValues = !!facetValuesRequestMade[selectedFacet] && !showingMoreValues[selectedFacet];

  const dropdownBody = (
    <FacetDetails
      facet={selectedFacetData}
      close={closeDetailsPopup}
      totalMatches={facetData?.matchCount || 0}
      totalSamples={facetData?.sampledEventCount || 0}
      getMoreFacetValues={getMoreFacetValues}
      fetchingMoreValues={fetchingMoreValues}
      showingMoreValues={showingMoreValues[selectedFacet]}
    />
  );

  const popperConfig = {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 9],
        },
      },
    ],
  };

  // function to run when the details popup wants to hide on outside click; if that click was on a facet,
  // we want to show its details rather than close the popup
  const onHideFacetDetails = (event?: Event | SyntheticEvent): void => {
    const eventTarget = event?.target as HTMLElement;
    const facetEl = eventTarget.closest('[data-facet]') as HTMLDivElement;
    const facet = facetEl?.dataset?.facet;
    if (facet) {
      setSelectedFacet(facet);
    } else {
      closeDetailsPopup();
    }
  };

  const isPending = loadingFacets && !isComplex && !hasErrors;
  const isResolved = requestCanceled || isComplex || hasErrors;
  const facetListWithoutSource = facetList.filter((facet => facet.name !== view.sources?.sourceField));

  let facetListContent: JSX.Element | null = (
    <OverlayTrigger
      placement="right-start"
      overlay={dropdownBody}
      popperConfig={popperConfig}
      className={classNames({
        Sidebar__FacetListContainer: !isStandardView,
        'Sidebar__FacetListContainer--standard': isStandardView,
        'dimming-overlay-effect': isPending,
      })}
      showOverlay={isFacetDetailsVisible}
      setShowOverlay={setShowOverlay}
      onHide={onHideFacetDetails}
    >
      <FacetList
        listRef={listRef}
        facetList={facetListWithoutSource}
        facetFilter={facetFilter}
        onFilterChange={(newFilter): void => setFacetFilter(newFilter)}
        onFacetSelect={onFacetSelect}
        onFacetHover={onFacetHover}
        onKeyDown={onKeyDown}
        selectedFacet={selectedFacet}
      />
    </OverlayTrigger>
  );

  /* Handle resolved status */
  useEffect(() => {
    if (isResolved) {
      setSidebarStatus(K.COMPONENT_STATUS.RESOLVED);
    }
  }, [isResolved, setSidebarStatus]);

  if (isPending && !isPreviousDataAvailable) {
    facetListContent = <SidebarSkeleton />;
  }

  if (isResolved) {
    facetListContent = null;
  }

  const hasSingleSourceValue = view.sources?.sourceValues.length === 1;
  const handleSourceSelectorClick = () => {
    if (hasSingleSourceValue) {
      return;
    }
    setShowOverlay((current) => !current);
    onFacetSelect(view.sources?.sourceField);
  };
  const handleSourceSelectorMouseEnter = () => {
    if (hasSingleSourceValue) {
      return;
    }
    onFacetHover(view.sources?.sourceField);
  };
  const sourcesContent = view.sources
    ? <SourceSelector
      getSourcesData={() => getMoreFacetValues(view.sources?.sourceField, true)}
      sourceLabelPlural={view.sources.sourceLabelPlural}
      sourceValues={view.sources.sourceValues}
      onClick={handleSourceSelectorClick}
      onMouseEnter={handleSourceSelectorMouseEnter}
    />
    : <SearchFields sourcesAndLogs={sourcesAndLogs} />;

  const facetValuesRequestManager = facetValuesRequests.map((request) => <LRQRequestManager key={request.key} request={request} />);

  return (
    <div className="Sidebar">
      {sourcesContent}
      {facetListContent}
      {facetValuesRequestManager}
    </div>
  );
}

export default Sidebar;
